import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Typography } from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
import AddIcon from '@material-ui/icons/Add';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  AdvancedSearchableProjectDropdown,
  AdvancedSearchableTaskDropdown
} from '~/modules/common/components';
import { PROJECT_EXECUTION_PHASE } from '~/modules/common/enums';
import { useMeContext } from '~/modules/me';
import { useHasProjects } from './hooks/useHasProjects';
import ProjectTimeEntrySummary from './TimeEntrySummary';
import CriteriaItem from './CriteriaItem';
import AddCriteriaMenu from './AddCriteriaMenu';
import { useContentStyles } from './styles';
import { useSelectMoveTimeStep, useProjectManager } from './hooks';

const addIcon = <AddIcon />;

const SelectMoveTimeStep = ({
  moveTimeData,
  setMoveTimeData,
  onProjectChange,
  onTaskChange,
  onCriteriaChange,
  timeEntrySummary,
  initialProject,
  initialTask
}) => {
  const { uri: currentUserUri } = useMeContext();
  const classes = useContentStyles();
  const intl = useIntl();

  const {
    loading: projectManagerLoading,
    projectManagerUri
  } = useProjectManager({
    projectId: initialProject?.id
  });

  const { loading: projectCheckLoading, hasProjects } = useHasProjects({
    filter: {
      text: '',
      projectManagers: [currentUserUri]
    }
  });

  const hasNoProjects = !projectCheckLoading && !hasProjects;

  const isCurrentUserProjectManager = useMemo(() => {
    if (projectManagerLoading) return false;

    return projectManagerUri === currentUserUri;
  }, [projectManagerLoading, projectManagerUri, currentUserUri]);

  useEffect(() => {
    if (!projectManagerLoading && isCurrentUserProjectManager) {
      setMoveTimeData(prev => {
        return {
          ...prev,
          source: {
            ...prev.source,
            project: initialProject || null,
            task: initialTask?.isTimeEntryAllowed ? initialTask : null,
            filter: {
              ...prev.source.filter,
              showInactiveProjects:
                initialProject?.status?.uri ===
                  PROJECT_EXECUTION_PHASE.ARCHIVED || false,
              showClosedTasks: initialTask?.isClosed || false
            }
          }
        };
      });
    }
  }, [
    projectManagerLoading,
    isCurrentUserProjectManager,
    initialProject,
    initialTask,
    setMoveTimeData
  ]);

  const {
    handleCriteriaAdd,
    handleCriterionChange,
    createHandleRemove,
    menuAnchorEl,
    handleAddCriteriaClick,
    handleMenuClose,
    selectedTypes,
    allCriteriaAdded
  } = useSelectMoveTimeStep({
    moveTimeData,
    onCriteriaChange,
    onTaskChange
  });

  const buttonStyle = useMemo(
    () => ({ visibility: menuAnchorEl ? 'hidden' : 'visible' }),
    [menuAnchorEl]
  );

  return (
    <div className={classes.stepContent} data-qe-id="MoveTimeDialogSelectStep">
      <Typography variant="h6" className={classes.sectionTitle}>
        <FormattedMessage id="moveTimeDialog.selectTime.title" />
      </Typography>
      {projectCheckLoading ? (
        <div className={classes.formRow}>
          <Skeleton variant="rect" height={56} />
          <Skeleton variant="rect" height={56} />
        </div>
      ) : hasNoProjects ? (
        <Alert
          severity="error"
          icon={false}
          data-qe-id="NoProjectsAvailableError"
        >
          <Typography component="div" className={classes.warningTitle}>
            <FormattedMessage id="moveTimeDialog.noProjectsAvailable" />
          </Typography>
          <Typography component="div">
            <FormattedMessage id="moveTimeDialog.noProjectsPermissionMessage" />
          </Typography>
        </Alert>
      ) : (
        <div className={classes.formRow}>
          <AdvancedSearchableProjectDropdown
            value={moveTimeData.source.project || null}
            onChange={onProjectChange}
            label={intl.formatMessage({ id: 'moveTimeDialog.project' })}
            showInactiveProjects={
              initialProject?.status?.uri ===
                PROJECT_EXECUTION_PHASE.ARCHIVED || false
            }
            disabled={timeEntrySummary.loading || projectManagerLoading}
          />
          <AdvancedSearchableTaskDropdown
            value={
              projectManagerLoading ? null : moveTimeData.source.task || null
            }
            onChange={onTaskChange}
            label={intl.formatMessage({ id: 'moveTimeDialog.task' })}
            projectId={moveTimeData.source.project?.id}
            disabled={
              projectManagerLoading ||
              !moveTimeData.source.project ||
              timeEntrySummary.loading
            }
            showClosedTasks={initialTask?.isClosed || false}
          />
        </div>
      )}
      {!projectCheckLoading && !hasNoProjects && (
        <>
          <Typography
            variant="body1"
            gutterBottom
            className={classes.criteriaDescription}
          >
            <FormattedMessage id="moveTimeDialog.forTheFollowing" />
          </Typography>
          <Button
            startIcon={addIcon}
            className={classes.addCriteriaButton}
            color="primary"
            onClick={handleAddCriteriaClick}
            disabled={timeEntrySummary.loading || allCriteriaAdded}
            data-qe-id="MoveTimeDialogAddCriteriaButton"
            aria-label={intl.formatMessage({
              id: 'moveTimeDialog.addCriteria'
            })}
            style={buttonStyle}
          >
            <FormattedMessage id="moveTimeDialog.addCriteria" />
          </Button>
          {(moveTimeData.criteria || []).map(criterion => (
            <CriteriaItem
              key={criterion.id}
              criterion={criterion}
              onRemove={createHandleRemove(criterion.id)}
              onChange={handleCriterionChange}
              projectId={moveTimeData.source.project?.id}
              disabled={timeEntrySummary.loading}
            />
          ))}
          <AddCriteriaMenu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
            onSelect={handleCriteriaAdd}
            selectedTypes={selectedTypes}
          />
          <ProjectTimeEntrySummary
            projectName={moveTimeData.source.project?.displayText}
            taskDisplayText={
              moveTimeData.source.task?.fullPathText ||
              moveTimeData.source.task?.displayText
            }
            loading={timeEntrySummary.loading}
            count={timeEntrySummary.count}
            totalHours={timeEntrySummary.totalHours}
          />
        </>
      )}
    </div>
  );
};

SelectMoveTimeStep.propTypes = {
  moveTimeData: PropTypes.object.isRequired,
  setMoveTimeData: PropTypes.func.isRequired,
  onProjectChange: PropTypes.func.isRequired,
  onTaskChange: PropTypes.func.isRequired,
  onCriteriaChange: PropTypes.func.isRequired,
  timeEntrySummary: PropTypes.object.isRequired,
  initialProject: PropTypes.object,
  initialTask: PropTypes.object
};

export default SelectMoveTimeStep;
